// material-ui
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import MainCard from 'components/MainCard';

// assets
import { DownloadOutlined, EditOutlined, FilePdfOutlined, RedoOutlined, RightOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { openSnackbar } from 'store/reducers/snackbar';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { config as apiConfig, selectedProfile } from '../../../../config/config';
import { scopes } from 'config/scopes';

const formatDate = (value) => {
  if(!value) return null;
  try {
    return format(parseISO(value), 'yyyy-MM-dd HH:mm');
  }
  catch(e) {
    return value;
  }
}

const formatSize = (bytes) => {
  if(bytes === null || bytes === undefined) return null;
  if(bytes < 1024) return bytes + ' B';
  return Math.max(1, Math.round(bytes / 1024)) + ' KB';
}

/**
 * E-signature card of the client (FULL_FLOW partners): signature status of the contract package, the document list
 * (download, scope mitigate_documents) and, when the partner has the "manual e-signature link" switch, the
 * "generate e-signature link" button. The link is the same 7 day client portal link as the KYC one, only the
 * notification differs (linkType = SIGN); the portal then shows the signing step to the client.
 */
export default({model, clientId, personal, canSendEmail, canSendSms}) => {
  const dispatcher = useDispatch();
  const [t, i18n] = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [commMethod, setCommMethod] = useState('email');
  const [buttonEnabled, setButtonEnabled] = useState(true);
  const [sentAt, setSentAt] = useState(null);
  const [documents, setDocuments] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const canSeeDocuments = model.hasScope(scopes.SCOPE_MITIGATE_DOCUMENTS) === true;
  const kycPassed = personal?.kycStatus === 'DONE' && personal?.kycResult === 'PASSED';
  const signatureStatus = personal?.signatureStatus;
  const documentsGenerated = !!personal?.documentsGenerated;
  const rejectType = personal?.esignRejectType;
  const manual = personal?.esignLinkManual === true;
  const signed = signatureStatus === 'DONE';
  const failed = !signed && rejectType === 'FINAL';
  const emailAddress = personal?.email;
  const phoneNumber = personal?.phone;
  const linkGeneratedAt = sentAt ? sentAt : formatDate(personal?.esignLinkGenerated);
  const reviewedAt = formatDate(personal?.signatureReviewed);

  let statusKey = 'esign_status_not_started';
  let statusColor = 'default';
  if(signed) {
    statusKey = 'esign_status_signed';
    statusColor = 'success';
  }
  else if(failed) {
    statusKey = 'esign_status_failed';
    statusColor = 'error';
  }
  else if(rejectType === 'RETRY') {
    statusKey = 'esign_status_retry';
    statusColor = 'warning';
  }
  else if(documentsGenerated) {
    statusKey = 'esign_status_waiting';
    statusColor = 'warning';
  }
  else if(kycPassed) {
    statusKey = 'esign_status_documents_pending';
    statusColor = 'info';
  }

  let description = t('esign_desc_kyc_needed');
  if(signed) {
    description = t('esign_desc_signed');
  }
  else if(failed) {
    description = t('esign_desc_failed');
  }
  else if(!kycPassed) {
    description = t('esign_desc_kyc_needed');
  }
  else if(!documentsGenerated) {
    description = t('esign_desc_documents_pending');
  }
  else if(manual && linkGeneratedAt) {
    description = t('esign_desc_link_generated', {date: linkGeneratedAt});
  }
  else if(manual) {
    description = t('esign_desc_manual');
  }
  else {
    description = t('esign_desc_automatic');
  }

  // the manual link button: only for partners with the switch, after the KYC, while the package is generated but not signed yet
  const canGenerate = manual && kycPassed && !signed && documentsGenerated;

  const loadDocuments = () => {
    if(!clientId || !canSeeDocuments) {
      return;
    }
    model.fetchClientDocuments(dispatcher, clientId).unwrap().then(res => {
      if(res?.success) {
        setDocuments(res.data?.documents ? res.data.documents : []);
      }
      else {
        setDocuments([]);
        dispatcher(openSnackbar({open: true, message: t('esign_documents_error'), variant: 'alert', alert: {color: 'error'}}));
      }
    }, e => {
      setDocuments([]);
      dispatcher(openSnackbar({open: true, message: t('esign_documents_error'), variant: 'alert', alert: {color: 'error'}}));
    });
  }

  useEffect(() => {
    loadDocuments();
    // re-fetch when the signature state of the client changes (e.g. the signed copy appears after the client details refresh)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, canSeeDocuments, signatureStatus, personal?.documentsGenerated]);

  const downloadDocument = (doc) => {
    if(!clientId || !doc?.id) {
      return;
    }
    setDownloadingId(doc.id);
    model.downloadClientDocument(dispatcher, clientId, doc.id, doc.fileName).unwrap().then(res => {
      setDownloadingId(null);
      if(res?.success && res.objectUrl) {
        const anchor = document.createElement('a');
        anchor.href = res.objectUrl;
        anchor.download = res.fileName ? res.fileName : (doc.fileName ? doc.fileName : 'document.pdf');
        anchor.style.display = 'none';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        setTimeout(() => URL.revokeObjectURL(res.objectUrl), 10000);
      }
      else {
        dispatcher(openSnackbar({open: true, message: t('esign_download_error'), variant: 'alert', alert: {color: 'error'}}));
      }
    }, e => {
      setDownloadingId(null);
      dispatcher(openSnackbar({open: true, message: t('esign_download_error'), variant: 'alert', alert: {color: 'error'}}));
    });
  }

  const startProcess = () => {
    if(!clientId) {
      return;
    }
    setButtonEnabled(false);
    const sendEmail = commMethod == 'email';
    const sendSms = commMethod == 'sms';
    model.fetchClientAccessToken(dispatcher, clientId, sendEmail, sendSms, 'SIGN').unwrap().then(res => {
      setButtonEnabled(true);
      setDialogOpen(false);
      if(res.success) {
        dispatcher(openSnackbar({open: true, message: t('esign_link_generated'), variant: 'alert', alert: {color: 'success'}}));
        const portalLink = apiConfig[selectedProfile].MITIGATE_CLIENT_PORTAL_URL+"?client="+res.data.access_token;
        if(commMethod == 'clipboard') {
          navigator.clipboard.writeText(portalLink);
        }
        setSentAt(format(new Date(), 'yyyy-MM-dd HH:mm'));
        // refresh the client details so esignLinkGenerated / signatureStatus are up to date
        model.fetchDetails(dispatcher, clientId);
      }
      else {
        dispatcher(openSnackbar({open: true, message: t('esign_link_generation_error'), variant: 'alert', alert: {color: 'error'}}));
      }
    }, e => {
      setButtonEnabled(true);
      setDialogOpen(false);
      dispatcher(openSnackbar({open: true, message: t('esign_link_generation_error'), variant: 'alert', alert: {color: 'error'}}));
    });
  }

  const documentSecondary = (doc) => {
    const parts = [];
    if(doc.fileName) parts.push(doc.fileName);
    const size = formatSize(doc.sizeBytes);
    if(size) parts.push(size);
    const generatedAt = formatDate(doc.generatedAt);
    if(generatedAt) parts.push(t('esign_document_generated_at', {date: generatedAt}));
    const signedAt = formatDate(doc.signedAt);
    if(doc.signed && signedAt) parts.push(t('esign_document_signed_at', {date: signedAt}));
    return parts.join(' · ');
  }

  const showDocuments = canSeeDocuments && (documentsGenerated || (documents && documents.length > 0));

  return (
    <>
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <MainCard title={t('esign_title')} sx={{background: signed ? '#EDFFEF' : (failed ? '#FFF1F0' : '#F4F8FF')}}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <EditOutlined style={{ fontSize: '150%'}} />
                <Chip label={t(statusKey)} color={statusColor} size="small" variant={statusColor === 'default' ? 'outlined' : 'filled'} />
                {reviewedAt && signed &&
                  <Typography variant="caption">{t('esign_reviewed_at', {date: reviewedAt})}</Typography>
                }
              </Stack>
              <Typography sx={{marginTop: 2}}>{description}</Typography>
              {rejectType && !signed &&
                <Typography variant="caption" sx={{display: 'block', marginTop: 1}}>{t('esign_reject_type', {type: rejectType})}</Typography>
              }
              {showDocuments &&
                <>
                  <Typography variant="subtitle1" sx={{marginTop: 3}}>{t('esign_documents')}</Typography>
                  {documents && documents.length === 0 &&
                    <Typography variant="caption">{t('esign_no_documents')}</Typography>
                  }
                  {documents && documents.length > 0 &&
                    <List dense disablePadding>
                      {documents.map(doc => (
                        <ListItem key={'client_document_'+doc.id} disableGutters
                          secondaryAction={
                            <Tooltip title={t('esign_download')}>
                              <span>
                                <IconButton edge="end" color="primary" aria-label={t('esign_download')} disabled={downloadingId === doc.id} onClick={() => downloadDocument(doc)}>
                                  <DownloadOutlined />
                                </IconButton>
                              </span>
                            </Tooltip>
                          }>
                          <FilePdfOutlined style={{fontSize: '140%', marginRight: 12}} />
                          <ListItemText
                            primaryTypographyProps={{component: 'div'}}
                            secondaryTypographyProps={{component: 'div'}}
                            primary={
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Typography variant="subtitle2">{doc.label ? doc.label : doc.fileName}</Typography>
                                {doc.version &&
                                  <Chip label={t('esign_document_version', {version: doc.version})} size="small" variant="outlined" />
                                }
                                <Chip label={doc.signed ? t('esign_document_signed') : t('esign_document_unsigned')} size="small" color={doc.signed ? 'success' : 'default'} variant={doc.signed ? 'filled' : 'outlined'} />
                              </Stack>
                            }
                            secondary={documentSecondary(doc)}
                          />
                        </ListItem>
                      ))}
                    </List>
                  }
                </>
              }
              {canGenerate &&
                <Grid container spacing={0} direction="row" sx={{marginTop: 3}} justifyContent="flex-start">
                  <Button variant="shadow" disabled={!buttonEnabled} color="info" onClick={() => setDialogOpen(true)} endIcon={linkGeneratedAt ? <RedoOutlined /> : <RightOutlined />}>
                    {linkGeneratedAt ? t('esign_regenerate_link') : t('esign_generate_link')}
                  </Button>
                </Grid>
              }
            </MainCard>
          </Grid>
        </Grid>
      </Grid>
      <Dialog maxWidth="sm" fullWidth onClose={() => setDialogOpen(false)} open={dialogOpen} sx={{ '& .MuiDialog-paper': { p: 0 } }}>
        <DialogTitle>{t('esign_generate_link')}</DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 2.5 }}>
          <Typography sx={{marginBottom: 2}}>{t('esign_generate_link_desc')}</Typography>
          <FormControl fullWidth>
            <InputLabel id="esign-communication-label" htmlFor="esign-communication">{t('send_link_to')}</InputLabel>
            <Select
              labelId="esign-communication-label"
              id="esign-communication"
              onChange={(event) => setCommMethod(event.target.value)}
              value={commMethod}
              label={t('method_of_communication')}>
              {canSendEmail && emailAddress &&
                <MenuItem value={'email'}>{t('email_to_address', {'emailAddress': emailAddress})}</MenuItem>
              }
              {canSendSms && phoneNumber &&
                <MenuItem value={'sms'}>{t('sms_to_number', {'number': phoneNumber})}</MenuItem>
              }
              <MenuItem value={'clipboard'}>{t('copy_to_clipboard')}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5 }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item></Grid>
            <Grid item>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button color="error" onClick={() => setDialogOpen(false)}>
                  {t('cancel')}
                </Button>
                <Button type="submit" variant="contained" disabled={!buttonEnabled} onClick={startProcess}>
                  {t('save')}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </>
  );
}
